import { AlertCircle } from 'lucide-react';
/**
 * RouteErrorBoundary - Error boundary specifically for route components
 * Provides graceful error handling and recovery for individual routes
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';
import { Button } from '../ui/Button';
import { createRouteLogger } from '../../utils/logger';

// ============================================================================
// Types
// ============================================================================

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error | null;
  errorInfo?: ErrorInfo | null;
}

// ============================================================================
// Styled Components
// ============================================================================

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: var(--space-2xl);
  text-align: center;
  background: var(--surface);
  border-radius: var(--radius-lg);
  margin: var(--space-xl);
`;

const ErrorIcon = styled.div`
  font-size: 1.75rem;
  margin-bottom: var(--space-lg);
  opacity: 0.7;
`;

const ErrorTitle = styled.h2`
  margin: 0 0 var(--space-md) 0;
  color: var(--text-primary);
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
`;

const ErrorMessage = styled.p`
  margin: 0 0 var(--space-xl) 0;
  color: var(--text-muted);
  font-size: var(--font-size-lg);
  line-height: var(--line-height-relaxed);
  max-width: 500px;
`;

const ErrorActions = styled.div`
  display: flex;
  gap: var(--space-md);
  flex-wrap: wrap;
  justify-content: center;
`;

const ErrorDetails = styled.details`
  margin-top: var(--space-xl);
  padding: var(--space-lg);
  background: var(--neutral-25);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  text-align: left;
  max-width: 600px;
  width: 100%;

  summary {
    cursor: pointer;
    font-weight: var(--font-weight-semibold);
    color: var(--text-muted);
    font-size: var(--font-size-sm);
    margin-bottom: var(--space-sm);

    &:hover {
      color: var(--text-primary);
    }
  }

  pre {
    font-size: var(--font-size-sm);
    color: var(--text-muted);
    white-space: pre-wrap;
    word-break: break-word;
    margin: var(--space-sm) 0 0 0;
    background: var(--surface);
    padding: var(--space-md);
    border-radius: var(--radius-sm);
    border: 1px solid var(--border);
    overflow-x: auto;
    max-height: 200px;
    overflow-y: auto;
  }
`;

// ============================================================================
// Route Error Boundary Component
// ============================================================================

const routeErrorBoundaryLogger = createRouteLogger('RouteErrorBoundary');

export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    routeErrorBoundaryLogger.error(
      'RouteErrorBoundary derived state failure',
      error,
      {
        url: window.location.href,
        timestamp: new Date().toISOString(),
      },
    );
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    routeErrorBoundaryLogger.error('RouteErrorBoundary caught error', error, {
      componentStack: errorInfo.componentStack,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    });

    // Check if this is a navigation-related error or styled-components error that we can recover from
    const isNavigationError =
      error.message.includes('Navigation') ||
      error.message.includes('router') ||
      error.stack?.includes('navigate');

    const isStyledComponentsError =
      error.message.includes('styled-components') ||
      error.message.includes('github.com/styled-components') ||
      (error.stack?.includes('styled-components') &&
        error.message.includes('See https://'));

    routeErrorBoundaryLogger.debug('RouteErrorBoundary error classification', {
      isNavigationError,
      isStyledComponentsError,
      errorMessage: error.message,
      hasNavigateInStack: error.stack?.includes('navigate'),
      isStyledComponentsInStack: error.stack?.includes('styled-components'),
    });

    // For navigation errors or styled-components errors during navigation, try to recover gracefully
    if (isNavigationError || isStyledComponentsError) {
      const errorType = isNavigationError ? 'Navigation' : 'Styled-components';
      routeErrorBoundaryLogger.warn('RouteErrorBoundary attempting recovery', {
        errorType,
        errorMessage: error.message,
      });

      // For styled-components errors, give more time for CSS variables to stabilize
      const recoveryDelay = isStyledComponentsError ? 300 : 100;

      setTimeout(() => {
        routeErrorBoundaryLogger.info(
          'RouteErrorBoundary retrying after recovery delay',
          {
            errorType,
            recoveryDelay,
          },
        );
        this.setState({ hasError: false, error: null, errorInfo: null });
      }, recoveryDelay);
      return;
    }

    routeErrorBoundaryLogger.error(
      'RouteErrorBoundary displaying fallback UI',
      error,
    );
    // In a real app, you might want to send this to a service like Sentry
    // reportError(error, { context: 'route', errorInfo });

    this.setState({ error, errorInfo });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReportIssue = () => {
    // In a real app, you might want to open a feedback form or issue tracker
    const subject = encodeURIComponent('Error Report - Route Error');
    const body = encodeURIComponent(`
Error Details:
${this.state.error?.name}: ${this.state.error?.message}

Stack Trace:
${this.state.error?.stack}

Component Stack:
${this.state.errorInfo?.componentStack}

User Agent: ${navigator.userAgent}
URL: ${window.location.href}
Timestamp: ${new Date().toISOString()}
    `);

    window.open(`mailto:support@example.com?subject=${subject}&body=${body}`);
  };

  override render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <ErrorContainer>
          <ErrorIcon>
            <AlertCircle size={28} aria-hidden />
          </ErrorIcon>
          <ErrorTitle>Algo salió mal</ErrorTitle>
          <ErrorMessage>
            Ha ocurrido un error inesperado al cargar esta página. Puedes
            intentar recargar la página o volver al inicio. Si el problema
            persiste, por favor reporta el issue.
          </ErrorMessage>

          <ErrorActions>
            <Button variant="primary" size="md" onClick={this.handleRetry}>
              🔄 Intentar de nuevo
            </Button>

            <Button variant="secondary" size="md" onClick={this.handleReload}>
              🔄 Recargar página
            </Button>

            <Button variant="tertiary" size="md" onClick={this.handleGoHome}>
              🏠 Ir al inicio
            </Button>

            <Button
              variant="tertiary"
              size="sm"
              onClick={this.handleReportIssue}
            >
              📧 Reportar problema
            </Button>
          </ErrorActions>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <ErrorDetails>
              <summary>Detalles del error (solo en desarrollo)</summary>
              <pre>
                {this.state.error.name}: {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
                {this.state.errorInfo?.componentStack &&
                  `\n\nComponent Stack:\n${this.state.errorInfo.componentStack}`}
              </pre>
            </ErrorDetails>
          )}
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary;
